import { useEffect, useMemo, useRef, useState } from "react"

import { Trans, useI18next, useTranslation } from "gatsby-plugin-react-i18next"
import gsap from "gsap"
import styled, { css } from "styled-components"

import Button from "components/buttons/Button"
import HeroVideo from "components/HeroVideo"
import InnerWrapper from "components/InnerWrapper"
import { ReactComponent as GalileoLogoSVG } from "images/logos/galileoFull.svg"
import colors from "styles/colors"
import text from "styles/text"
import { fmobile, fresponsive, ftablet } from "utils/fullyResponsive"
import useMedia from "utils/useMedia"
import { returnContactUsLink } from "utils/routes"

const desktopWebM = "HomeHero3D.webm"
const desktopMP4 = "HomeHero3D.mp4"
const mobileWebM = "HomeHero3DM.webm"
const mobileMP4 = "HomeHero3DM.mp4"

export default function Hero() {
  const [activeWordIndex, setActiveWordIndex] = useState(0)
  const [canPlay, setCanPlay] = useState(true)
  const [title, setTitle] = useState<HTMLDivElement | null>(null)
  const wordContainer = useRef<HTMLDivElement | null>(null)
  const [cycleWords, setCycleWords] = useState<JSX.Element[] | null>(null)
  const { t } = useTranslation()
  const { language } = useI18next()

  const wordOptions = t(
    "leaders. builders. founders. entrepreneurs. innovators."
  )

  const mp4Vid = useMedia(desktopMP4, desktopMP4, desktopMP4, mobileMP4)
  const webmVid = useMedia(desktopWebM, desktopWebM, desktopWebM, mobileWebM)
  const sources = useMemo(() => [webmVid, mp4Vid], [webmVid, mp4Vid])

  const allWordOptions = useMemo(
    () =>
      wordOptions.split(" ").map((word, i) => {
        const num = i
        return (
          <ExtraWord className={`word-${i}`} key={`${word}-${num}`}>
            {word}
          </ExtraWord>
        )
      }),
    [wordOptions]
  )

  useEffect(() => {
    setCycleWords(allWordOptions)
  }, [allWordOptions])

  const wordAnimationStep = (nextWord: number, currentWord: number) => {
    const tl = gsap.timeline()
    tl.to(`.word-${currentWord}`, { y: "-110%", duration: 0.4 }, 0.4)
    tl.to(`.word-${nextWord}`, { y: "0", duration: 0.4 }, 0.4)
    tl.to(
      `.word-${currentWord}`,
      {
        y: "110%",
        duration: 0,
        onComplete: () => {
          setActiveWordIndex(nextWord)
        },
      },
      1.6
    )
  }

  useEffect(() => {
    if (title && wordContainer.current && cycleWords && canPlay) {
      const currentWord = activeWordIndex
      const nextWord =
        currentWord === cycleWords.length - 1 ? 0 : currentWord + 1

      wordAnimationStep(nextWord, currentWord)
    }
  }, [activeWordIndex, canPlay, cycleWords, title])

  useEffect(() => {
    if (title) {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: title,
          start: "top bottom",
          scrub: true,
          end: "bottom top",
          onLeave: () => {
            setCanPlay(false)
          },
          onEnterBack: () => {
            setCanPlay(true)
          },
        },
      })

      tl.to(title, { opacity: 1 })

      return () => {
        tl.kill()
      }
    }
  }, [title])

  const titleTop: JSX.Element[] = t("Expanding the financial frontier with")
    .split(" ")
    .map((word, i) => {
      const num = i
      return <p key={`${word}-${num}`}>{word}</p>
    })
  {
    if (language === "pt") {
      titleTop.push(
        <WordContainerPt key="cycleWords" ref={wordContainer}>
          {cycleWords}
        </WordContainerPt>
      )
    } else {
      titleTop.push(
        <WordContainer key="cycleWords" ref={wordContainer}>
          {cycleWords}
        </WordContainer>
      )
    }
  }

  // console.log("LANGUAGE", language)
  // console.log("TYPEOF", typeof language)

  return (
    <Wrapper>
      <BGImage>
        <HeroVideo className="homepage-heroVideo" sources={sources} />
      </BGImage>

      <InnerWrapper>
        <Galileo />
        {language === "pt" ? (
          <TitlePt ref={ref => setTitle(ref)}>{titleTop}</TitlePt>
        ) : (
          <Title ref={ref => setTitle(ref)}>{titleTop}</Title>
        )}
        <Body>
          <Trans>
            We&apos;re a proven financial technology platform, an innovation
            engine and a leader in card issuing, payments and digital banking.
            Every day, we partner with big thinkers like you to reinvent the way
            people interact with their money and build a better financial future
            for us all.
          </Trans>
        </Body>
        <Button styledLink href={returnContactUsLink(language)} arrow>
          <Trans>Let&apos;s Talk</Trans>
        </Button>
      </InnerWrapper>
    </Wrapper>
  )
}

const ExtraWord = styled.p`
  position: absolute;
  transform: translateY(110%);
`

const WordContainer = styled.div`
  position: absolute;
  overflow: hidden;
  bottom: 0;

  ${fresponsive(css`
    height: 96px;
    width: 581px;
    left: 180px;
  `)}

  .word-0 {
    transform: translateY(0);
  }

  ${ftablet(css`
    width: 461px;
    height: 64px;
    left: 123px;
    bottom: 6px;
  `)}

  ${fmobile(css`
    position: relative;
    display: inline-block;
    width: 261px;
    height: 49px;
    bottom: -1px;
    left: 0;
  `)}
`

const WordContainerPt = styled.div`
  position: absolute;
  overflow: hidden;
  bottom: 0;

  ${fresponsive(css`
    height: 96px;
    width: 781px;
    left: 220px;
  `)}

  .word-0 {
    transform: translateY(0);
  }

  ${ftablet(css`
    width: 561px;
    height: 64px;
    left: 150px;
    bottom: 6px;
  `)}

  ${fmobile(css`
    position: relative;
    display: inline-block;
    width: 281px;
    height: 49px;
    bottom: -1px;
    left: 0;
  `)}
`

const Wrapper = styled.div`
  width: 100%;

  display: flex;
  position: relative;
  justify-content: center;

  ${fresponsive(css`
    height: 1365px;
    padding-top: 210px;
  `)}

  ${ftablet(css`
    height: 1122px;
    padding-top: 250px;
  `)}

  ${fmobile(css`
    height: 712px;
    padding-top: 120px;
  `)}
`

const BGImage = styled.div`
  position: absolute;
  overflow: hidden;
  width: 100%;
  height: 100%;
  background-color: ${colors.blue800};

  top: 0;
  left: 0;

  ${fmobile(css`
    height: 712px;
  `)}
`

const Galileo = styled(GalileoLogoSVG)`
  position: absolute;

  ${fresponsive(css`
    width: 144px;
    height: 30px;
    top: -120px;
  `)}

  ${ftablet(css`
    top: -175px;
  `)}

  ${fmobile(css`
    width: 122px;
    height: 25px;
    top: -82px;
  `)}
`

const Title = styled.div`
  position: relative;
  ${text.h2};
  color: ${colors.white};
  display: flex;
  flex-wrap: wrap;

  ${fresponsive(css`
    margin-bottom: 20px;
    width: 730px;
    gap: 0 20px;
  `)}

  ${ftablet(css`
    ${text.h3};
    width: 520px;
    gap: 0 15px;
  `)}

  ${fmobile(css`
    gap: 0 10px;
    ${text.h1Mobile};
    margin-bottom: 10px;
    width: 335px;
    height: 123px;
  `)}
`

const TitlePt = styled.div`
  position: relative;
  ${text.h2};
  color: ${colors.white};
  display: flex;
  flex-wrap: wrap;

  ${fresponsive(css`
    margin-bottom: 20px;
    width: 830px;
    gap: 0 20px;
  `)}

  ${ftablet(css`
    ${text.h3};
    width: 520px;
    gap: 0 15px;
  `)}

  ${fmobile(css`
    gap: 0 10px;
    ${text.h1Mobile};
    margin-bottom: 10px;
    width: 335px;
    height: 163px;
  `)}
`

const Body = styled.p`
  ${text.bodyR};
  color: ${colors.blue10};

  ${fresponsive(css`
    margin-bottom: 40px;
    width: 627px;
  `)}

  ${ftablet(css`
    width: 485px;
  `)}

  ${fmobile(css`
    width: 335px;
  `)}
`
